import styled from "@emotion/styled";
import { Box, MenuItem, Select, type SelectChangeEvent, type SxProps, Typography } from "@mui/material";
import { ReactComponent as ArrowUpIcon } from "Components/_Shared/Icons/Assets/arrow-up.svg";
import { BlCheckboxInput } from "Components/_Shared/Inputs/BlCheckboxInput";
import { StyledFormLabelWrapper } from "Forms/Components/FormStyles";
import type { LookupItem } from "Infrastructure/Models/LookupItem";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import * as React from "react";
import type { JSX } from "react";
import { Flex } from "../Core/Flex";

type valueType = string | number | string[] | number[];

type Props = {
  name?: string;
  options: LookupItem[];
  value: valueType;
  defaultValue?: valueType;
  onChange?: (value: valueType) => void;
  onBlur?: () => void;
  disabled?: boolean;
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  allSelectedText?: string;
  hasError?: boolean;
  hasFilterStyle?: boolean;
  labelEndAdornment?: JSX.Element;
  icon?: React.ReactNode;
  sx?: SxProps;
  direction?: "row" | "column";
};

// Styled components
const StyledSelect = styled(Select)<{ $hasFilterStyle?: boolean }>`
  &:before,
  &:after {
    content: none;
  }

  height: 40px;

  &.Mui-disabled {
    background-color: ${(props) => props.theme.colors.primaryDisabled};
    color: ${(props) => props.theme.colors.textDisabled};
  }

  .MuiSelect-select {
    padding: ${(props) => props.theme.spacing(props.$hasFilterStyle ? 0 : 1.4, props.$hasFilterStyle ? 0 : 1.6)};
    padding-right: 6px !important;
    border-radius: ${(props) => props.theme.spacing(Spacing.M)};
  }

  .MuiSelect-nativeInput {
    border-width: 0px;   
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.colors.border} !important;
    border-radius: ${(props) => props.theme.spacing(Spacing.M)};
  }


  ${(props) =>
    props.$hasFilterStyle &&
    `
    .MuiSelect-select {
      color: ${props.theme.palette.primary.main};
      background-color: transparent;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  `}
`;

const StyledIcon = styled.div<{ $hasFilterStyle: boolean }>`
  position: absolute;
  right: 12px;
  pointer-events: none;
  display: flex;
  align-items: center;
  color: ${(props) => (props.$hasFilterStyle ? props.theme.palette.primary.main : props.theme.palette.text.primary)};

  svg {
    width: 20px;
    height: auto;
    opacity: ${(props) => (props.$hasFilterStyle ? 1 : 0.5)};
  }
`;

const StyledSelectedValue = styled.div<{
  $hasFilterStyle: boolean;
  $upperCase?: boolean;
}>`
  display: flex;
  overflow: hidden;
  padding-left: ${(props) => props.theme.spacing(1)};
  margin-right: ${(props) => (props.$hasFilterStyle ? props.theme.spacing(1) : props.theme.spacing(2))};
  text-overflow: ellipsis;
  text-transform: ${(props) => (props.$upperCase ? "uppercase" : "none")};
  min-height: 14px;
`;

const dropdownIcon = (hasFilterStyle: boolean) => {
  return (
    <StyledIcon $hasFilterStyle={hasFilterStyle}>
      <Box sx={{ transform: "rotate(180deg)", display: "flex" }}>
        <ArrowUpIcon />
      </Box>
    </StyledIcon>
  );
};

const StyledPlaceholder = styled.div<{ $hasFilterStyle: boolean }>`
  font-size: ${(props) => (props.$hasFilterStyle ? "12px" : "16px")};
  margin-right: ${(props) => (props.$hasFilterStyle ? props.theme.spacing(1) : props.theme.spacing(2))};
  font-weight: ${(props) => (props.$hasFilterStyle ? 600 : 400)};
  opacity: ${(props) => (props.$hasFilterStyle ? 1 : 0.5)};
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 16px;
  .MuiTouchRipple-root {
    color: ${(props) => props.theme.palette.primary.main};
  }

  .MuiCheckbox-root {
    padding: 0;
  }
`;

const selectedValue = (
  value: valueType,
  props: Props,
  t: (resourcePath: string, options?: any) => string,
  icon?: React.ReactNode,
): React.ReactNode => {
  const { placeholder, multiple, options, allSelectedText } = props;

  if (!value || (value as Array<string | number>).length === 0) {
    return placeholder ? (
      <StyledPlaceholder $hasFilterStyle={props.hasFilterStyle ?? false}>{placeholder}</StyledPlaceholder>
    ) : (
      <StyledSelectedValue $hasFilterStyle={props.hasFilterStyle ?? false} />
    );
  }

  if (multiple && (value as Array<string | number>).length === options.length) {
    return (
      <StyledSelectedValue $hasFilterStyle={props.hasFilterStyle ?? false}>
        {allSelectedText ?? t(Resources.Common.AllStatuses)}
      </StyledSelectedValue>
    );
  }
  const text = multiple
    ? options
        .filter((o) => (value as number[]).some((v) => v === o.Value))
        .map((x) => x.Name)
        .join(", ")
    : options.find((x) => x.Value.toString() === value.toString())?.Name;

  return (
    <StyledSelectedValue $hasFilterStyle={props.hasFilterStyle ?? false}>
      <Box display="flex" alignItems="center" mr={2}>
        {icon}
      </Box>
      {text}
    </StyledSelectedValue>
  );
};

export const BlDropdown: React.FunctionComponent<Props> = (props) => {
  const {
    options,
    value,
    disabled,
    name,
    onChange,
    multiple,
    label,
    onBlur,
    hasError,
    hasFilterStyle,
    direction = "column",
    labelEndAdornment,
    icon,
    sx,
  } = props;
  const { t } = useResource();
  const menuAnchorRef = React.useRef<HTMLDivElement>(null);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    onChange?.(event.target.value as valueType);
  };

  const anchorMenuProps =
    hasFilterStyle && menuAnchorRef.current
      ? {
          anchorEl: menuAnchorRef.current,
        }
      : {};

  return (
    <Flex flexDirection={direction} gap={direction === "row" ? Spacing.XXL : 0} width={"100%"}>
      {label && (
        <StyledFormLabelWrapper style={{ whiteSpace: "nowrap", flexShrink: 0, alignItems: "center" }}>
          <Typography fontWeight={500} variant="body2" mr={Spacing.S}>
            {label}
          </Typography>
          {labelEndAdornment}
        </StyledFormLabelWrapper>
      )}
      <StyledSelect
        sx={sx}
        value={value}
        defaultValue={props.defaultValue}
        onChange={handleChange}
        onBlur={onBlur}
        inputProps={{ "aria-label": "Without label" }}
        IconComponent={() => dropdownIcon(hasFilterStyle ?? false)}
        disabled={disabled}
        multiple={multiple}
        error={hasError}
        fullWidth
        displayEmpty
        MenuProps={{
          variant: "menu",
          style: {
            maxHeight: "300px",
          },
          ...anchorMenuProps,
        }}
        renderValue={(value) => selectedValue(value as valueType, { ...props, sx }, t, icon)}
        variant={"outlined"}
        $hasFilterStyle={hasFilterStyle}
      >
        {options.map((opt) => (
          <StyledMenuItem key={`${name}-${opt.Name}-${opt.Value}`} value={opt.Value}>
            {multiple ? (
              <BlCheckboxInput checked={(value as number[]).some((x) => x === opt.Value)} label={opt.Name} />
            ) : (
              <span>{opt.Name}</span>
            )}
          </StyledMenuItem>
        ))}
      </StyledSelect>
      <div ref={hasFilterStyle ? menuAnchorRef : undefined} />
    </Flex>
  );
};
